import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { cn } from 'src/lib/utils';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

export type ChatToastVariant = 'default' | 'success' | 'warning' | 'destructive';

export type ChatToastOptions = {
  id?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: ChatToastVariant;
  duration?: number; // ms
  // Mode: 'auto' closes by time (default). 'trigger' waits for external close callback.
  mode?: 'auto' | 'trigger';
  // When in 'trigger' mode, this callback receives a `close()` function to dismiss the toast.
  attach?: (close: () => void) => void;
};

export type ChatToastApi = {
  push: (options: ChatToastOptions) => string; // returns id
  clear: () => void;
  // True when at least one chat toast is visible
  isToastActive: boolean;
  // Closes all trigger-mode toasts immediately
  trigger: () => void;
};

type InternalToast = Required<
  Pick<ChatToastOptions, 'id' | 'title' | 'description' | 'variant' | 'duration'>
> & {
  createdAt: number;
  mode: NonNullable<ChatToastOptions['mode']>;
};

type ChatToastContextValue = ChatToastApi & {
  toasts: InternalToast[];
  remove: (id: string) => void;
};

const ChatToastContext = createContext<ChatToastContextValue | null>(null);

export const useChatToast = (): ChatToastApi => {
  const ctx = useContext(ChatToastContext);
  if (!ctx) {
    throw new Error('useChatToast must be used within <ChatToastProvider>');
  }
  return { push: ctx.push, clear: ctx.clear, isToastActive: ctx.isToastActive, trigger: ctx.trigger };
};

export const ChatToastProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [toasts, setToasts] = useState<InternalToast[]>([]);
  const [isToastActive, setIsToastActive] = useState<boolean>(false);

  const remove = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((options: ChatToastOptions) => {
    const id = options.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const toast: InternalToast = {
      id,
      title: options.title ?? '',
      description: options.description ?? '',
      variant: options.variant ?? 'default',
      duration: Math.max(1200, options.duration ?? 2400),
      createdAt: Date.now(),
      mode: options.mode ?? 'auto',
    };

    setToasts((prev) => [...prev, toast]);
    // If trigger mode, hand back a close function via attach callback
    if ((options.mode ?? 'auto') === 'trigger') {
      options.attach?.(() => remove(id));
    }
    return id;
  }, []);

  const clear = useCallback(() => setToasts([]), []);

  // Remove all trigger-mode toasts
  const trigger = useCallback(() => {
    setToasts((prev) => prev.filter((t) => t.mode !== 'trigger'));
  }, []);

  React.useEffect(() => {
    setIsToastActive(toasts.length > 0);
  }, [toasts]);

  const value = useMemo(
    () => ({ toasts, push, clear, remove, isToastActive, trigger }),
    [toasts, push, clear, remove, isToastActive, trigger],
  );

  return (
    <ChatToastContext.Provider value={value}>
      {children}
    </ChatToastContext.Provider>
  );
};

/**
 * ChatToastViewport renders absolute-positioned toast(s) within the closest relatively
 * positioned container. It is designed to appear "behind" the input (lower z-index)
 * and slide upward from the bottom edge.
 */
export const ChatToastViewport: React.FC<{
  className?: string;
  zIndexClassName?: string; // e.g., 'tw-z-0' to stay behind input
  offsetBottom?: number; // px offset from bottom
  anchorId?: string; // element id to position above (uses its height)
  closeOnClickAway?: boolean;
}> = ({ className, zIndexClassName = 'tw-z-0', offsetBottom = 8, anchorId, closeOnClickAway = false }) => {
  const ctx = useContext(ChatToastContext);
  if (!ctx) return null;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [bottom, setBottom] = React.useState<number>(offsetBottom);

  React.useEffect(() => {
    if (!anchorId) {
      setBottom(offsetBottom);
      return;
    }

    const el = document.getElementById(anchorId);
    if (!el) {
      setBottom(offsetBottom);
      return;
    }

    const update = () => {
      const h = el.getBoundingClientRect()?.height || (el as HTMLElement).clientHeight || 0;
      setBottom(h + offsetBottom);
    };

    update();

    let ro: ResizeObserver | undefined;
    if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
      ro = new ResizeObserver(() => update());
      ro.observe(el);
    }

    const onResize = () => update();
    window.addEventListener('resize', onResize);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [anchorId, offsetBottom]);

  // Close trigger-mode toasts when clicking outside the anchor element and the toast viewport
  React.useEffect(() => {
    if (!closeOnClickAway) return;

    const handler = (e: MouseEvent) => {
      // Only act if there are trigger-mode toasts
      const hasTrigger = ctx.toasts.some((t) => t.mode === 'trigger');
      if (!hasTrigger) return;

      const anchorEl = anchorId ? document.getElementById(anchorId) : null;
      const viewportEl = containerRef.current;

      const x = e.clientX;
      const y = e.clientY;

      const isInsideRect = (rect: DOMRect | null | undefined) =>
        !!rect && x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

      const anchorRect = anchorEl?.getBoundingClientRect() ?? null;
      const viewportRect = viewportEl?.getBoundingClientRect() ?? null;

      // Ignore clicks within anchor or within the toast viewport area
      if (isInsideRect(anchorRect) || isInsideRect(viewportRect)) return;

      // Clicked outside; close trigger-mode toasts
      ctx.trigger();
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [closeOnClickAway, anchorId, ctx]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'tw-pointer-events-none tw-absolute tw-inset-x-4',
        zIndexClassName,
        className,
      )}
      style={{ bottom }}
    >
      <div className="tw-relative tw-mx-auto tw-flex tw-max-w-[640px] tw-flex-col tw-gap-2">
        {ctx.toasts.map((t) => (
          <SingleChatToast key={t.id} toast={t} onDone={() => ctx.remove(t.id)} />
        ))}
      </div>
    </div>
  );
};

function getVariantIcon(variant: ChatToastVariant) {
  switch (variant) {
    case 'success':
      return <CheckCircle2 size={20} className="tw-text-green-500" />;
    case 'destructive':
      return <AlertCircle size={20} className="tw-text-red-500" />;
    case 'warning':
      return <AlertCircle size={20} className="tw-text-yellow-400" />;
    default:
      return null;
  }
}

const SingleChatToast: React.FC<{
  toast: InternalToast;
  onDone: () => void;
}> = ({ toast, onDone }) => {
  const [phase, setPhase] = useState<'enter' | 'shown' | 'leave'>('enter');
  const hideTimer = useRef<number | null>(null);
  const tickTimer = useRef<number | null>(null);

  React.useEffect(() => {
    // kick off enter transition on mount
    const r = requestAnimationFrame(() => setPhase('shown'));

    // Auto-hide only in 'auto' mode
    if (toast.mode === 'auto' && toast.duration > 0) {
      hideTimer.current = window.setTimeout(() => setPhase('leave'), toast.duration);
    }

    // cleanup
    return () => {
      cancelAnimationFrame(r);
      if (hideTimer.current) clearTimeout(hideTimer.current);
      if (tickTimer.current) clearTimeout(tickTimer.current);
    };
  }, [toast.duration]);

  const handleTransitionEnd = () => {
    if (phase === 'leave') onDone();
  };

  // Match regular toast container styling
  const base =
    'tw-group tw-pointer-events-auto tw-relative tw-flex tw-min-h-14 tw-w-full tw-items-center tw-justify-between tw-space-x-4 tw-overflow-hidden tw-rounded-lg tw-border tw-bg-neutral-800 tw-px-2.5 tw-py-4 tw-text-white tw-shadow-lg tw-transition-all';

  // slide from behind input (bottom) upwards a bit while fading in, then fade out and move further up
  const transform =
    phase === 'enter'
      ? 'translateY(100%) scale(0.98)'
      : phase === 'shown'
      ? 'translateY(-12px) scale(1)'
      : 'translateY(-64px) scale(0.98)';
  const opacity = phase === 'enter' ? 0 : phase === 'shown' ? 1 : 0;

  return (
    <div
      className={cn(base)}
      style={{
        transition: 'transform 300ms ease, opacity 300ms ease',
        transform,
        opacity,
      }}
      onTransitionEnd={handleTransitionEnd}
      role="status"
      aria-live="polite"
    >
      <div className="tw-flex tw-gap-2">
        {getVariantIcon(toast.variant)}
        <div className="tw-flex tw-flex-col tw-gap-1">
          {toast.title ? (
            <div className="tw-font-semibold tw-leading-tight">{toast.title}</div>
          ) : null}
          {toast.description ? (
            <div className="tw-font-medium">{toast.description}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ChatToastProvider;
